import {IWixSdk} from '@wix/js-sdk-wrapper';
import {MusicAlbumCollection} from '@wix/music-shared/typings/music-collection';
import {decorate, inject, injectable} from 'inversify';
import {services} from '../../ioc/services';
import {BI_ERRORS} from '../../logging/errors';
import {logError} from '../../logging/logging';
import {DEMO_ALBUM_ID, NO_ALBUM_ID} from '../../widget-player/constants';
import {ICollectionFetcher} from '../CollectionFetcher/ICollectionFetcher';
import {IGlobal} from '../Global/IGlobal';
import {SiteStorage} from '../SiteStorage/SiteStorage';
import {ICollectionManager} from './ICollectionManager';
import {LegacyData, RawLegacyData} from './types';

export class CollectionManager implements ICollectionManager {
  constructor(
    private WixSdk: IWixSdk,
    private global: IGlobal,
    private readonly collectionFetcher: ICollectionFetcher,
    private readonly siteStorage: SiteStorage,
  ) {}

  static isNotEmptyAlbumId = (id: string | undefined): id is string => Boolean(id) && id !== NO_ALBUM_ID;

  static isEmptyId = (id: string | undefined) => !CollectionManager.isNotEmptyAlbumId(id);

  async getCollection(): Promise<{from: string; collection: MusicAlbumCollection; settings?: any}> {
    try {
      return await this.getCollectionImpl();
    } catch (reason) {
      logError(BI_ERRORS.widget.musicAlbum.get, {reason});
      throw reason;
    }
  }

  private async getCollectionImpl() {
    const metaSiteId = this.WixSdk.Utils.getInstanceValue('metaSiteId');

    const siteStoredCollectionId = await this.siteStorage.get('albumId');

    if (CollectionManager.isNotEmptyAlbumId(siteStoredCollectionId)) {
      const preLoadedCollection = this.global.getPreLoadedCollection();
      if (preLoadedCollection && preLoadedCollection.id === siteStoredCollectionId) {
        return {from: 'externalId', collection: preLoadedCollection};
      }
      // desync. Collection from style params is more preferedable.
      // One of the desync reason - user selected new album and then selected album with old id. Then external id will
      // lead to previously selected album and in style params will be old id
      return {
        from: 'siteParams',
        collection: await this.collectionFetcher.fetchCollection(siteStoredCollectionId, metaSiteId),
      };
    }

    const [demoCollection, {collection: legacyCollection, settings: legacySettings}] = await Promise.all([
      this.getDemoAlbum(),
      this.getLegacyCollection(),
    ]);

    if (legacyCollection) {
      return {from: 'legacy-settings', collection: legacyCollection, settings: legacySettings};
    }
    if (legacySettings) {
      return {from: 'demoAlbum-withLegacy', collection: demoCollection, settings: legacySettings};
    }
    return {from: 'demoAlbum', collection: demoCollection};
  }

  private async getDemoAlbum(): Promise<MusicAlbumCollection> {
    const demoAlbumId: string = (await this.siteStorage.get('demoAlbumId')) || DEMO_ALBUM_ID;
    return this.collectionFetcher.fetchCollection(demoAlbumId, null);
  }

  private async getLegacyCollection(): Promise<{settings?: LegacyData; collection?: MusicAlbumCollection}> {
    const compId = this.WixSdk.Utils.getCompId();
    const instanceId = this.WixSdk.Utils.getInstanceId();
    let response: RawLegacyData;
    try {
      response = await this.collectionFetcher.fetchLegacyCollection(instanceId, compId);
    } catch (reason) {
      return {};
    }

    const {fonts = {}, numbers = {}, booleans = {}} = response;
    const albumIdFont = fonts.albumId;
    const albumId = albumIdFont && albumIdFont.value;
    const collection = albumId ? await this.collectionFetcher.fetchCollection(albumId, null) : undefined;
    return {
      collection,
      settings: {
        fonts: {
          'wmp::albumId': albumIdFont,
          'wmp::paymentsId': fonts.paymentsId,
        },
        numbers,
        booleans,
      },
    };
  }
}

decorate(injectable(), CollectionManager);
decorate(inject(services.WixSdk) as ParameterDecorator, CollectionManager, 0);
decorate(inject(services.global) as ParameterDecorator, CollectionManager, 1);
decorate(inject(services.collectionFetcher) as ParameterDecorator, CollectionManager, 2);
decorate(inject(services.siteStorage) as ParameterDecorator, CollectionManager, 3);
